// ------------------------------------------------------------------------------
// name: datetime
// author: mudas
// created: 2022/10/18
// ------------------------------------------------------------------------------

import dayjs, { ConfigType } from 'dayjs';
import { isArray, isDate, isNumber } from 'lodash-es';

/**
 * 日期描述信息
 *
 * 包括 `year、month、day、hour、minute、second`
 */
export interface DateDetail {
  year?: number;
  month?: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

/**
 * 解析字符串时间
 * @param {String} value 如：06:00或者06:00:00（同时支持 '2021-02-01 06:00:00' 的完整日期字符串）
 * @return {{ year, month, day, hour, minute, second }} 年，月，日，小时，分钟，秒数
 *
 * @example
 *
 * parseDate('06:00'); // now at '2021-03-02 16:00'
 * // => { year:2021, month:3, day:2, hour:6, minute:0, second:0 }
 *
 * parseDate('2021-03-02 16:00:00');
 * // => { year:2021, month:3, day:2, hour:16, minute:0, second:0 }
 */
export const parseDate = (value: string): DateDetail => {
  const ts = value.split(':');
  const length = ts.length;
  let ymd: Date;

  if (ts[0].indexOf(' ') !== -1) {
    const split = ts[0].split(/\s+/);
    ymd = dayjs(split[0]).toDate();
    ts[0] = split[1];
  } else {
    ymd = new Date();
  }

  const hour = parseInt(ts[0], 10);
  const minute = parseInt(ts[1], 10);
  const second = length === 3 ? parseInt(ts[2], 10) : 0;

  return { year: ymd.getFullYear(), month: ymd.getMonth() + 1, day: ymd.getDate(), hour, minute, second };
};

/**
 * 根据指定的自选参数，生成日期对象
 * <p>该方法的参数一般可使用 parseDate 进行得到。</p>
 * @param option 日期时间参数
 * @param {Number} [option.year] 年
 * @param {Number} [option.month] 月
 * @param {Number} [option.day] 日
 * @param {Number} [option.hour] 小时
 * @param {Number} [option.minute] 分钟
 * @param {Number} [option.second] 秒
 * @return {Date}
 *
 * @example
 *
 * genDate({year:2021, month:3, day:2, hour:16});
 * // => Date
 */
export const genDate = ({ year, month, day, hour, minute, second }: Partial<DateDetail>): Date => {
  const date: Date = new Date();

  if (typeof year === 'number') date.setFullYear(year);
  if (typeof month === 'number') date.setMonth(month - 1);
  if (typeof day === 'number') date.setDate(day);

  if (typeof hour === 'number') date.setHours(hour);
  if (typeof minute === 'number') date.setMinutes(minute);
  if (typeof second === 'number') date.setSeconds(second);

  return date;
};

/**
 * 检测当前时间是否在指定两个日期时间范围内（毫秒级对比）
 * @param {String|String[]} dateRange 指定时间范围（如 '00:00~23:59'）
 * @param {Number|Date} [now=Date.now()] 待检测目标时间（默认取当前时间）
 * @return {boolean}
 *
 * @example
 * betweenDate('08:08:59~23:08:59');
 * // => true // now at '10:08:59'
 *
 * betweenDate('2024-01-24 08:08:59~2024-01-24 23:08:59');
 * // => true // now at '2024-01-24 10:08:59'
 *
 * betweenDate('2023-01-24 08:08:59~23:08:59');
 * // => false // now at '2024-01-24 10:08:59'
 * // 第二部分时间未指定日期，会默认以当前 `2024-01-24` 为准
 */
export const betweenDate = (dateRange: string | string[], now: number | Date = Date.now()): boolean => {
  if (dateRange) {
    const t = isArray(dateRange) ? dateRange : dateRange.split(/[~]/i);
    const d1 = genDate(parseDate(t[0]));
    const d2 = genDate(parseDate(t[1]));

    const ms1 = d1.getTime();
    let ms2 = d2.getTime();

    // 检测 d2 是否同设置成 00:00
    // d2 向前推进24小时
    if (ms1 === ms2 && d2.getHours() === 0 && d2.getMinutes() === 0) {
      ms2 += 86400000; // 加1天的毫秒数
    }

    const nowNum = isDate(now) ? now.getTime() : isNumber(now) ? now : Date.now();

    return ms1 < nowNum && nowNum < ms2;
  }
  return false;
};

/**
 * 格式化日期信息为指定格式
 * @param dateInfo 日期数据信息
 * @param format 目标格式（默认：'YYYY-MM-DD HH:mm:ss'），具体参考[format](https://dayjs.gitee.io/docs/zh-CN/display/format)
 *
 * @example
 *
 * dateformat('2021/02/20 08:08:59');
 * // => 2021-02-20 08:08:59
 *
 * dateformat('2021/02/20 08:08:59', 'YYYY-MM-DD HH:mm:ss');
 * // => 2021-02-20 08:08:59
 *
 * dateformat(1611961835705, 'YYYY-MM-DD HH:mm:ss');
 * // => 2021-01-30 07:10:35
 */
export const dateformat = (dateInfo?: string | number | Date | null, format = 'YYYY-MM-DD HH:mm:ss') => {
  return dayjs(!dateInfo ? undefined : dateInfo).format(format);
};

/**
 * 解析秒数包含的时间单位
 * - 返回结果不包含 year 和 month 的计算，最大单位为 day（天）
 *
 * @param seconds 秒数单位
 * @return {{ year, month, day, hour, minute, second }} 年，月，天，小时，分钟，秒
 *
 * @example
 *
 * parseDate(70);
 * // => { year:0, month:0, day:0, hour:0, minute:1, second:10 }
 * // => 00:01:10
 */
export const parseSeconds = (seconds: number): DateDetail => {
  const r: DateDetail = { year: 0, month: 0, day: 0, hour: 0, minute: 0, second: 0 };

  let cur = seconds || 0;

  if (cur >= 86400) {
    r.day = Math.floor(cur / 86400);
    cur %= 86400;
  }

  if (cur >= 3600) {
    r.hour = Math.floor(cur / 3600);
    cur %= 3600;
  }

  if (cur >= 60) {
    r.minute = Math.floor(cur / 60);
    cur %= 60;
  }

  r.second = cur >> 0;

  return r;
};

/**
 * 将秒转化为相应的时间字符串
 * @param seconds 秒数
 * @param [strategy='all'] 输出结果的策略（'max'=只输出最大值，忽略末尾 | 'all'=全部，忽略前置的0值 | 'short'=全部，忽略中间的0值）
 * @param [unit={ day: '天', hour: '小时', minute: '分', second: '秒' }] 默认后缀单位
 * @return 当所有值为 0 时返回 ''，不会返回具体 0 值时间，若有需要请自行外部处理默认显示
 *
 * @example
 *
 * formatSeconds(88200);
 * // => '1天0小时30分0秒'
 * // 不会显示前导 0，如 '0天1小时30分0秒' 会输出 '1小时30分0秒'
 *
 * formatSeconds(88200, 'all');
 * // => '1天0小时30分0秒'
 *
 * formatSeconds(88200, 'short');
 * // => '1天30分'
 *
 * formatSeconds(88200, 'max');
 * // => '1天'
 */
export function formatSeconds(
  seconds: number,
  strategy: 'max' | 'all' | 'short' = 'all', // 定义策略：取最大值、全部、全部-简短模式去除中间0
  unit = { day: '天', hour: '小时', minute: '分', second: '秒' }
) {
  const parsed = parseSeconds(seconds);
  const data = [
    { value: parsed.day, unit: unit.day },
    { value: parsed.hour, unit: unit.hour },
    { value: parsed.minute, unit: unit.minute },
    { value: parsed.second, unit: unit.second }
  ];

  const filter = ({ value }) => typeof value === 'number' && value > 0;

  if (strategy === 'max') {
    const max = data.find(({ value }) => typeof value === 'number' && value > 0);
    // 返回第一个最大值，若皆不存在则默认返回 '0秒'
    return max ? `${max.value}${max.unit}` : '';
  } else if (strategy === 'short') {
    return data
      .filter(filter)
      .map(({ value, unit }) => `${value}${unit}`)
      .join('');
  } else {
    if (data.filter(filter).length === 0) return '';

    const firstIndex = data.findIndex(filter);
    return data
      .slice(firstIndex)
      .map(({ value, unit }) => `${value}${unit}`)
      .join('');
  }
}

/**
 * 在指定数字不足2位时，在前补足0占位
 * @param value
 *
 * @example
 *
 * twoDigits(2);
 * // => '02'
 */
export const twoDigits = (value: number): string => {
  const base = `0${value}`;
  return base.substring(base.length - 2);
};

/**
 * 计算两个日期之间的秒数，再转换为时间字符串
 * @param start 开始日期
 * @param end 结束日期
 * @param [strategy='all'] 输出结果的策略（'max'=只输出最大值 | 'all'=全部 | 'short'=全部，忽略中间的0值）
 *
 * @example
 *
 * duration('2024-07-23 14:00:00', '2024-07-24 14:30:00');
 * // => '1天0小时30分0秒'
 *
 * duration('2024-07-23 14:00:00', '2024-07-24 14:30:00', 'all');
 * // => '1天0小时30分0秒'
 *
 * formatSeconds('2024-07-23 14:00:00', '2024-07-24 14:30:00', 'short');
 * // => '1天30分'
 *
 * formatSeconds('2024-07-23 14:00:00', '2024-07-24 14:30:00', 'max');
 * // => '1天'
 */
export const duration = (start: ConfigType, end: ConfigType, strategy: 'max' | 'all' | 'short' = 'all') => {
  const diff = Math.abs(dayjs(end).diff(dayjs(start), 'second')); // 转换成秒
  return formatSeconds(diff, strategy);
};

/**
 * 格式化日期选择器产出的 dayjs 数组为 [string, string]
 * @param date
 * @param format
 */
export const formatDatePickDate = (date?: dayjs.Dayjs[], format?: string) => {
  if (!date) return undefined;

  const [startTime, endTime] = date;
  const dates: [string, string] = ['', ''];

  if (startTime) dates[0] = startTime.format(format);
  if (endTime) dates[1] = endTime.format(format);

  return dates;
};